import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Search, Plus, Layers, Check, BookMarked } from "lucide-react";
import { api } from "../lib/api";
import { euro } from "../lib/format";
import { priceSuffix } from "../lib/units";
import { CategoryBadge, ITEM_TYPE_LABELS, ITEM_TYPE_BADGE } from "./CategoryBadge";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Checkbox } from "./ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

export function itemToQuoteLine(it) {
  return {
    id: crypto.randomUUID(),
    title: it.title || "",
    description: it.description || "",
    unit_price: Number(it.unit_price) || 0,
    unit: it.unit || "corpo",
    quantity: Number(it.quantity) || 1,
    work_days: Number(it.work_days) || 0,
    work_unit: "giorni",
    item_type: it.item_type || "base",
    min_qty: Number(it.min_qty) || 1,
    max_qty: Number(it.max_qty) || 10,
    enabled_default: it.enabled_default !== false,
    discount_type: it.discount_type || "none",
    discount_value: Number(it.discount_value) || 0,
    iva_rate: it.iva_rate == null ? 22 : Number(it.iva_rate),
  };
}

export function groupToQuoteGroup(g) {
  return {
    id: crypto.randomUUID(),
    title: g.title || "",
    description: g.description || "",
    packages: (g.packages || []).map((p) => ({
      id: crypto.randomUUID(),
      name: p.name || "",
      subtitle: p.subtitle || "",
      features: Array.isArray(p.features) ? [...p.features] : [],
      price: Number(p.price) || 0,
      discount_type: p.discount_type || "none",
      discount_value: Number(p.discount_value) || 0,
      work_days: Number(p.work_days) || 0,
      iva_rate: p.iva_rate == null ? 22 : Number(p.iva_rate),
      recommended: !!p.recommended,
      default_selected: !!p.default_selected,
    })),
  };
}

const TYPE_FILTERS = [
  { v: "all", label: "Tutti" },
  { v: "base", label: "Voci Base" },
  { v: "optional", label: "Add-on" },
  { v: "tier", label: "Pacchetti Tier" },
];

export default function ImportFromCatalog({ open, onOpenChange, onImport }) {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selItems, setSelItems] = useState({});
  const [selGroups, setSelGroups] = useState({});

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelItems({}); setSelGroups({});
    (async () => {
      const [c, i, g] = await Promise.all([
        api.get("/catalog/categories"),
        api.get("/catalog/items"),
        api.get("/catalog/groups"),
      ]);
      setCategories(c.data); setItems(i.data); setGroups(g.data);
      setLoading(false);
    })();
  }, [open]);

  const showItems = typeFilter !== "tier";
  const showGroups = typeFilter === "all" || typeFilter === "tier";

  const fItems = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter((it) => {
      if (catFilter !== "all" && it.category !== catFilter) return false;
      if (typeFilter === "base" && !(it.item_type === "base" || it.item_type === "variable")) return false;
      if (typeFilter === "optional" && it.item_type !== "optional") return false;
      if (s && !(`${it.title} ${it.description}`.toLowerCase().includes(s))) return false;
      return true;
    });
  }, [items, search, catFilter, typeFilter]);

  const fGroups = useMemo(() => {
    const s = search.trim().toLowerCase();
    return groups.filter((g) => {
      if (catFilter !== "all" && g.category !== catFilter) return false;
      if (s && !(`${g.title} ${g.description}`.toLowerCase().includes(s))) return false;
      return true;
    });
  }, [groups, search, catFilter]);

  const selectedCount = Object.values(selItems).filter(Boolean).length + Object.values(selGroups).filter(Boolean).length;

  const importSingleItem = (it) => { onImport([it], []); };
  const importSingleGroup = (g) => { onImport([], [g]); };

  const importSelected = () => {
    const its = items.filter((it) => selItems[it.id]);
    const grs = groups.filter((g) => selGroups[g.id]);
    onImport(its, grs);
    setSelItems({}); setSelGroups({});
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0 flex flex-col" data-testid="import-catalog-sheet">
        <SheetHeader className="p-5 border-b border-slate-100">
          <SheetTitle className="flex items-center gap-2"><BookMarked style={{ width: 18, height: 18 }} className="text-primary" /> Importa dal Listino</SheetTitle>
          <SheetDescription>Inserisci servizi, add-on e pacchetti salvati nel tuo catalogo. Ogni elemento viene copiato come istanza indipendente.</SheetDescription>
        </SheetHeader>

        <div className="p-5 space-y-3 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="import-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca nel listino…" className="pl-9" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger data-testid="import-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte le categorie</SelectItem>
                {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger data-testid="import-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TYPE_FILTERS.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {loading ? (
            <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
          ) : (
            <>
              {showItems && fItems.map((it) => (
                <div key={it.id} data-testid={`import-item-${it.id}`} className="border border-slate-200 rounded-lg p-3 flex gap-3">
                  <Checkbox data-testid={`import-item-check-${it.id}`} checked={!!selItems[it.id]}
                    onCheckedChange={(v) => setSelItems((s) => ({ ...s, [it.id]: !!v }))} className="mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <CategoryBadge name={it.category} categories={categories} />
                      <Badge variant="outline" className={`${ITEM_TYPE_BADGE[it.item_type]} text-[10px]`}>{ITEM_TYPE_LABELS[it.item_type]}</Badge>
                    </div>
                    <p className="font-semibold text-sm text-slate-900 leading-snug">{it.title}</p>
                    <p className="text-xs text-slate-500 line-clamp-1">{it.description}</p>
                    <p className="font-mono text-sm font-bold text-slate-900 mt-1">{euro(it.unit_price)} <span className="text-xs font-normal text-slate-400">{priceSuffix(it.unit)}</span></p>
                  </div>
                  <Button size="sm" variant="outline" data-testid={`import-item-add-${it.id}`} onClick={() => importSingleItem(it)} className="self-center shrink-0">
                    <Plus style={{ width: 14, height: 14 }} className="mr-1" /> Inserisci
                  </Button>
                </div>
              ))}

              {showGroups && fGroups.map((g) => (
                <div key={g.id} data-testid={`import-group-${g.id}`} className="border border-indigo-100 bg-indigo-50/30 rounded-lg p-3 flex gap-3">
                  <Checkbox data-testid={`import-group-check-${g.id}`} checked={!!selGroups[g.id]}
                    onCheckedChange={(v) => setSelGroups((s) => ({ ...s, [g.id]: !!v }))} className="mt-1" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <Badge variant="outline" className="bg-indigo-100 text-indigo-700 border-indigo-200 text-[10px]"><Layers style={{ width: 11, height: 11 }} className="mr-1" /> Pacchetti Tier</Badge>
                      <CategoryBadge name={g.category} categories={categories} />
                    </div>
                    <p className="font-semibold text-sm text-slate-900 leading-snug">{g.title}</p>
                    <p className="text-xs text-slate-500">{(g.packages || []).length} opzioni · da {euro(Math.min(...(g.packages || [{ price: 0 }]).map((p) => p.price || 0)))}</p>
                  </div>
                  <Button size="sm" variant="outline" data-testid={`import-group-add-${g.id}`} onClick={() => importSingleGroup(g)} className="self-center shrink-0">
                    <Plus style={{ width: 14, height: 14 }} className="mr-1" /> Inserisci
                  </Button>
                </div>
              ))}

              {!loading && (showItems ? fItems.length : 0) + (showGroups ? fGroups.length : 0) === 0 && (
                <p className="text-sm text-slate-400 text-center py-16">Nessun elemento nel listino per questi filtri.</p>
              )}
            </>
          )}
        </div>

        {selectedCount > 0 && (
          <div className="p-4 border-t border-slate-100 bg-card">
            <Button className="w-full h-11 font-semibold" data-testid="import-selected-button" onClick={importSelected}>
              <Check style={{ width: 16, height: 16 }} className="mr-2" /> Importa {selectedCount} element{selectedCount === 1 ? "o" : "i"} selezionat{selectedCount === 1 ? "o" : "i"} nel preventivo
            </Button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
